import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ACOMODACOES, CATEGORIAS } from "@/data/acomodacoes";
import { brl } from "@/lib/pricing";
import { Gallery } from "@/components/Gallery";
import { Reveal, Eyebrow } from "@/components/Motion";

const Card = ({ a, idx }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.8, delay: (idx % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`accommodation-card-${a.slug}`}
    className="group flex flex-col"
  >
    <div className="relative">
      <Gallery fotos={a.fotos} alt={a.nome} aspect="aspect-[4/5]" testid={`card-gallery-${a.slug}`} />
      <span className="pointer-events-none absolute left-4 top-4 z-10 text-5xl font-bold tracking-[-0.05em] text-white/90 drop-shadow">
        {a.slug.slice(-2)}
      </span>
      {a.ilustrativas && (
        <span className="pointer-events-none absolute right-3 top-3 z-10 rounded-full bg-[#14291f]/70 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-[#f3efe4]">
          Fotos ilustrativas
        </span>
      )}
    </div>
    <div className="flex flex-1 flex-col pt-5">
      <p className="text-xs uppercase tracking-[0.22em] text-[#5b7a1a]">
        {a.categoria}
        {a.area ? ` · ${a.area}` : ""}
      </p>
      <Link to={`/acomodacao/${a.slug}`} data-testid={`accommodation-link-${a.slug}`} className="mt-2 flex items-start justify-between gap-4">
        <h3 className="text-2xl font-bold tracking-tight text-[#1a382b] transition-colors duration-300 group-hover:text-[#a85d38]">{a.nome}</h3>
        <ArrowUpRight className="mt-1 h-6 w-6 shrink-0 text-[#1a382b] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
      </Link>
      <ul className="mt-3 flex flex-wrap gap-2">
        {a.tags.slice(0, 3).map((t) => (
          <li key={t} className="rounded-full border border-[#d4ccb8] px-3 py-1 text-xs text-[#3d4a34]">
            {t}
          </li>
        ))}
      </ul>
      <div className="mt-auto flex items-end justify-between border-t border-[#d9d2c1] pt-4 mt-5">
        <p data-testid={`accommodation-price-${a.slug}`} className="text-[#1a382b]">
          <span className="text-xl font-bold">{brl(a.preco)}</span>
          <span className="text-sm text-[#5f6e62]"> / diária</span>
        </p>
        <Link
          to={`/acomodacao/${a.slug}#reserva`}
          data-testid={`accommodation-reserve-${a.slug}`}
          className="text-xs font-bold uppercase tracking-[0.16em] text-[#1a382b] underline decoration-[#7ab51d] decoration-2 underline-offset-[6px] transition-colors hover:text-[#a85d38]"
        >
          Reservar
        </Link>
      </div>
    </div>
  </motion.article>
);

export const Acomodacoes = () => {
  const [cat, setCat] = useState("Todas");
  const list = cat === "Todas" ? ACOMODACOES : ACOMODACOES.filter((a) => a.categoria === cat);

  return (
    <section id="acomodacoes" data-testid="accommodations-section" className="bg-[#ebe5d6] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-end">
          <Reveal>
            <Eyebrow>Acomodações</Eyebrow>
            <h2 className="max-w-2xl text-3xl font-bold leading-[1.02] tracking-[-0.035em] text-[#1a382b] sm:text-6xl">
              Escolha o seu canto na montanha.
            </h2>
          </Reveal>
          <div className="flex flex-wrap gap-2" data-testid="accommodations-filter">
            {CATEGORIAS.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`rounded-full px-5 py-2.5 text-sm transition-[background-color,color] duration-300 ${
                  cat === c ? "bg-[#1a382b] text-[#f3efe4]" : "border border-[#c9c0aa] text-[#1a382b] hover:bg-[#dcd4c1]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <motion.div layout className="mt-16 grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
          {list.map((a, i) => (
            <Card key={a.slug} a={a} idx={i} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
